import { useState } from "react";
import Sidebar from "../components/Sidebar";
import { Question, EnvelopeSimple, WhatsappLogo, Book, CaretDown, CaretRight } from "@phosphor-icons/react";

const FAQ = [
  { role: "bde", q: "How do I create a quotation?", a: "Go to Billing → Quotations → New. Link to a customer, add line items (use Product picker to auto-fill price + HSN), apply any discount, save. If discount > 5% or total > ₹1 lakh, submit it for approval." },
  { role: "bde", q: "My lead needs urgent follow-up, where do I track it?", a: "Leads page shows your assigned leads. Click a lead to add follow-ups, schedule next contact, and convert qualified leads into customers." },
  { role: "bde", q: "Why can't I approve my own quotation?", a: "Our approval chain uses maker-checker rules — the creator cannot approve their own document. A BDM or Manager must approve it." },
  { role: "bdm", q: "How do I see pending approvals for my team?", a: "Approvals Center → Billing tab shows all quotations in pending_bdm state. You can approve up to ₹5 lakh / 15% discount; higher goes to Manager." },
  { role: "bdm", q: "How do I reassign a lead?", a: "Open the lead detail, click 'Assign', select another BDE. Auto round-robin distribution happens on public enquiries." },
  { role: "manager", q: "How do I enable a blocked customer?", a: "Billing → Customers → open customer → change status from blocked to active. Credit limit can be updated in the same screen. An audit log captures the change." },
  { role: "manager", q: "How to trigger collection escalation scan?", a: "Billing → Sales Orders tab → 'Run Collection Scan' button. Overdue invoices auto-escalate: BDE (1-7 days), BDM (8-30), Manager (30+)." },
  { role: "admin", q: "How do I customize the invoice template?", a: "Billing → Company Settings → Template section. Change accent color, toggle HSN/discount/bank/signature columns, add custom footer text. Changes apply to all future prints." },
  { role: "admin", q: "How do I add a new role/permission?", a: "Roles are defined in code (ROLE_HIERARCHY + ROLE_PERMISSIONS). To add a new one, open /app/backend/server.py line ~51 and add the role + permissions, then redeploy." },
  { role: "admin", q: "Where are audit logs?", a: "GET /api/audit-logs?requester_role=admin returns all tracked changes (SO create/cancel, billing approvals, price book edits, product updates). Viewable by admin/manager/cfo." },
  { role: "all", q: "How does WhatsApp click-to-chat work?", a: "Every contact/lead shows a WhatsApp icon — clicking opens wa.me/<number> with a pre-filled greeting. No API token needed for this basic flow. For bulk marketing, connect WhatsApp Business in Social Hub." },
  { role: "all", q: "I forgot my password.", a: "On login, click 'Forgot password?' → enter email → check inbox for a reset link (60-minute expiry)." },
  { role: "all", q: "Why don't I see some pages?", a: "Navigation is role-gated. Admin sees everything; BDE sees only their daily workflow. Contact admin to upgrade role if needed." },
];

const HelpSupport = ({ user, logout }) => {
  const [open, setOpen] = useState({});
  const [search, setSearch] = useState("");
  const role = user?.role || "all";

  const filtered = FAQ.filter(f =>
    (f.role === role || f.role === "all") &&
    (!search || f.q.toLowerCase().includes(search.toLowerCase()) || f.a.toLowerCase().includes(search.toLowerCase()))
  );

  return (
    <div className="min-h-screen bg-slate-50" data-testid="help-support">
      <Sidebar user={user} logout={logout} />
      <div className="ml-64">
        <div className="bg-white border-b border-slate-200 px-6 py-5 flex items-center gap-3">
          <Question size={28} weight="duotone" className="text-[#002FA7]" />
          <div>
            <h1 className="text-xl font-bold">Help & Support</h1>
            <p className="text-xs text-slate-500">FAQs tailored to your role, plus direct support channels</p>
          </div>
        </div>

        <div className="p-6 max-w-4xl">
          <input data-testid="help-search" placeholder="Search FAQs…" value={search} onChange={e => setSearch(e.target.value)}
            className="w-full mb-4 px-4 py-3 border border-slate-300 rounded text-sm" />

          <div className="grid md:grid-cols-3 gap-3 mb-6">
            <a href="mailto:info@swiftechindia.com" className="bg-white border border-slate-200 p-4 rounded flex items-center gap-3 hover:border-[#002FA7]">
              <EnvelopeSimple size={24} weight="duotone" className="text-[#002FA7]" />
              <div><p className="text-xs text-slate-500 uppercase font-bold">Email Support</p><p className="text-sm font-semibold">info@swiftechindia.com</p></div>
            </a>
            <a href="https://wa.me/919876543210?text=Hi%2C%20I%20need%20help%20with%20the%20SwifTech%20platform" target="_blank" rel="noreferrer"
              className="bg-white border border-slate-200 p-4 rounded flex items-center gap-3 hover:border-[#25D366]">
              <WhatsappLogo size={24} weight="fill" className="text-[#25D366]" />
              <div><p className="text-xs text-slate-500 uppercase font-bold">WhatsApp</p><p className="text-sm font-semibold">Chat with support</p></div>
            </a>
            <a href="https://swiftechindia.com" target="_blank" rel="noreferrer"
              className="bg-white border border-slate-200 p-4 rounded flex items-center gap-3 hover:border-[#002FA7]">
              <Book size={24} weight="duotone" className="text-[#002FA7]" />
              <div><p className="text-xs text-slate-500 uppercase font-bold">Documentation</p><p className="text-sm font-semibold">swiftechindia.com</p></div>
            </a>
          </div>

          <h2 className="text-sm font-bold uppercase tracking-wider text-slate-600 mb-2">Frequently Asked</h2>
          <div className="space-y-2">
            {filtered.length === 0 && <p className="text-slate-500 py-6">No FAQs match your search.</p>}
            {filtered.map((f, i) => (
              <div key={i} className="bg-white border border-slate-200 rounded">
                <button data-testid={`faq-${i}`} onClick={() => setOpen(o => ({ ...o, [i]: !o[i] }))}
                  className="w-full text-left px-4 py-3 flex items-center justify-between hover:bg-slate-50">
                  <span className="font-semibold text-sm">{f.q}</span>
                  {open[i] ? <CaretDown size={16} /> : <CaretRight size={16} />}
                </button>
                {open[i] && <div className="px-4 pb-3 text-sm text-slate-700 border-t border-slate-100 pt-3">{f.a}</div>}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default HelpSupport;
